import {find, map} from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Dropdown, DropdownItem} from 'components';
import {useIsChangedTo} from 'hooks';
import {Option} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {OnlineStatus} from 'types';

type Props = {
    className?: string;
    dropdownOpen?: boolean;
    status?: OnlineStatus;
    icon?: boolean;
    disabled?: boolean;
    onDropdownClose?: () => void;
    onAvailabilityChange: (value: boolean) => void;
};

const T_KEYS_ONLINE_STATE = TRANSLATION_KEYS.COMPONENTS.ONLINE_STATE;

export const OnlineStatusSwitcher: React.FC<Props> = ({
    className, dropdownOpen: dropdownOpenProp, status, icon, disabled,
    onDropdownClose, onAvailabilityChange
}: Props) => {
    const {t} = useTranslation();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const isDropdownChangedToOpen = useIsChangedTo(dropdownOpenProp, true);

    const options = useMemo<Option<OnlineStatus>[]>(() => [
        {
            id: OnlineStatus.Online,
            name: t(T_KEYS_ONLINE_STATE.ONLINE),
        },
        {
            id: OnlineStatus.Away,
            name: t(T_KEYS_ONLINE_STATE.AWAY),
        }
    ], [t]);

    const selectedOption = useMemo(() => find(options, {id: status}), [status, options]);

    const openDropdown = useCallback(() => {
        setDropdownOpen(true);
    }, []);

    const closeDropdown = useCallback(() => {
        onDropdownClose?.();
        setDropdownOpen(false);
    }, [onDropdownClose]);

    const onChangeStatus = useCallback((value: OnlineStatus) => {
        onAvailabilityChange(value === OnlineStatus.Online);
        closeDropdown();
    }, [closeDropdown, onAvailabilityChange]);

    useEffect(() => {
        if (isDropdownChangedToOpen) {
            setDropdownOpen(true);
        }
    }, [isDropdownChangedToOpen]);

    return (
        <Dropdown
            open={dropdownOpen}
            controlled
            childrenClassName={className}
            triggerNode={
                <span>{selectedOption?.name || t(T_KEYS_ONLINE_STATE.OFFLINE)}</span>
            }
            icon={icon}
            disabled={disabled}
            onOpen={openDropdown}
            onClose={closeDropdown}
        >
            <div className='tw-w-36 tw-text-left'>
                {map(options, (option, index) => (
                    <DropdownItem
                        key={index}
                        selected={option.id === selectedOption?.id}
                        onClick={() => onChangeStatus(option.id)}
                    >
                        {option.name}
                    </DropdownItem>
                ))}
            </div>
        </Dropdown>
    );
};